import React, { useEffect, useState } from 'react';
import trackingDomainsService, { TrackingDomain } from '../../../services/trackingDomainsService';

const TrackingDomainsPanel: React.FC = () => {
  const [domains, setDomains] = useState<TrackingDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      setError(null);
      setPremiumBlocked(false);
      setLoading(true);
      const res = await trackingDomainsService.list();
      setDomains(res.domains || []);
    } catch (e: any) {
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
        setPremiumBlocked(true);
      } else {
        setError(e?.message || 'Failed to load tracking domains');
      }
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain) return;
    try {
      setSubmitting(true);
      await trackingDomainsService.create(newDomain.trim().toLowerCase());
      setNewDomain('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to add domain');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await trackingDomainsService.remove(id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete domain');
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await trackingDomainsService.verify(id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to verify domain');
    }
  };

  const handleHealth = async (id: string) => {
    try {
      const res = await trackingDomainsService.health(id);
      // show a simple toast-like message
      alert(`Health: ${res.status} (HTTPS: ${res.https_ok ? 'OK' : 'FAIL'})`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to check health');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Custom Tracking Domains</h3>
      </div>

      {premiumBlocked && (
        <div className="p-3 mb-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          Premium required to manage tracking domains.
        </div>
      )}

      {error && (
        <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          aria-label="New tracking domain"
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="e.g. track.yourdomain.com"
          className="flex-1 px-3 py-2 border border-gray-300 rounded"
          disabled={premiumBlocked || submitting}
        />
        <button
          type="submit"
          disabled={!newDomain || premiumBlocked || submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-60"
        >
          {submitting ? 'Adding...' : 'Add Domain'}
        </button>
      </form>

      {loading ? (
        <div className="h-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SSL</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Health</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {domains.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">No tracking domains yet.</td>
                </tr>
              ) : domains.map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{d.domain}</span>
                      <span className="text-xs text-gray-500">CNAME to {d.cname_target}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${d.verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {d.verified ? 'Verified' : 'Not Verified'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm text-gray-700">{d.ssl_status || 'unknown'}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm text-gray-700">{d.last_health || 'unknown'}</span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button onClick={() => handleVerify(d.id)} className="px-2 py-1 text-sm bg-indigo-600 text-white rounded">Verify</button>
                      <button onClick={() => handleHealth(d.id)} className="px-2 py-1 text-sm bg-gray-600 text-white rounded">Health</button>
                      <button onClick={() => handleDelete(d.id)} className="px-2 py-1 text-sm bg-red-600 text-white rounded">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrackingDomainsPanel;
